// One walk over a trace's shell statements, so an extractor is only its grammar.
//
// Four extractors were written before this existed and every one opened with the
// same forty lines: index tool_results by id, filter tool_use blocks to shell tool
// names, pull the command out of one of three input keys, resolve variables, strip
// ANSI, split into statements, classify the output as observed / backgrounded /
// shell-error, skip assignment and wrapper prefixes to find the program. Copied
// four times, it drifted four ways — one copy skipped `cd`'s argument, one didn't;
// one stopped at `--`, one didn't.
//
// So the shared part is here and the extractors keep only what makes them
// different: which programs they care about and what they read out of the
// arguments.
//
// THE OUTPUT BELONGS TO THE CALL, NOT THE STATEMENT. A tool result is one blob for
// a whole compound command, so every statement in `a && b && c` carries the same
// text and none of them can claim a specific line of it. Each statement is told how
// many siblings it had (`siblings`) so a grammar can refuse to attribute a result
// when it cannot be sure whose it is.

import {
  HARNESS_NOTICE_RE,
  SHELL_ERROR_RE,
  SHELL_TOOL_NAMES,
  resolveShellVars,
  splitStatements,
  stripAnsi,
  tokenize,
} from "./shell.js";

const str = (v) => (typeof v === "string" ? v : "");
const contentText = (c) => (typeof c === "string" ? c
  : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : str(p?.text))).join("\n") : str(c?.text));

const ASSIGNMENT_RE = /^\$?[\w.]+=/;
// `if`/`while`/`!` prefix a command without being one. Rejecting them as programs
// instead of skipping them DROPPED THE WHOLE STATEMENT — `if grep -q x f; then …`
// lost its grep, which is the exact shape the waits lens is built on.
const WRAPPER_RE = /^(?:sudo|env|command|nohup|time|exec|builtin|if|elif|while|until|then|else|do|!|\{|\()$/i;
// Wrappers that consume their own argument, which is why one copy of this logic
// reported a system called "backend" for `cd backend && cargo test`.
const WRAPPER_WITH_ARG_RE = /^(?:cd|pushd|source|\.|timeout|xargs)$/i;

const OUT_CAP = 4000;

/**
 * Every shell statement in a trace, with its call's result and its program already
 * resolved.
 *
 * @param {object[]} turns
 * @param {object} opts
 *   cap  max characters of result text carried per statement (default 4000)
 * @returns {object[]} {
 *   text, argv, program, args,        the statement
 *   output, outputStatus, ok,         the CALL's result — see the note above
 *   key, blockId, index, siblings,    identity
 * }
 */
export function shellStatements(turns, { cap = OUT_CAP } = {}) {
  const out = [];
  if (!Array.isArray(turns)) return out;

  const resultById = new Map();
  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }

  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      if (!SHELL_TOOL_NAMES.test(String(b.name || "").split("__").pop())) continue;
      const cmd = str(b.input?.command) || str(b.input?.cmd) || str(b.input?.script);
      if (!cmd) continue;

      const result = b.id ? resultById.get(b.id) : null;
      const errored = Boolean(result?.isError)
        || (result ? contentText(result.content).includes("<tool_use_error>") : false);
      const raw = result ? contentText(result.content) : "";
      // A harness notice or a shell error is not the program's output, and treating
      // it as such is how a lens reports the wrapper's words as the system's.
      const outputStatus = !raw ? "none"
        : HARNESS_NOTICE_RE.test(raw) ? "backgrounded"
        : SHELL_ERROR_RE.test(raw) ? "shell-error"
        : errored ? "tool-error"
        : "observed";
      const text = raw && (outputStatus === "observed" || outputStatus === "tool-error") ? stripAnsi(raw) : "";
      const output = text ? (text.length > cap ? text.slice(0, cap) : text) : null;
      // Kept even when it is not the program's output: an extractor sometimes needs
      // to read a refusal, and the status says which it is looking at.
      const notice = raw && !text ? stripAnsi(raw).slice(0, cap) : null;

      const statements = splitStatements(resolveShellVars(stripAnsi(cmd)));
      statements.forEach((st, i) => {
        const stText = st.text.trim();
        if (!stText || stText.startsWith("#")) return;
        const argv = tokenize(stText).filter(Boolean);
        let j = 0;
        while (j < argv.length) {
          const tok = argv[j];
          if (ASSIGNMENT_RE.test(tok)) { j += 1; continue; }
          if (WRAPPER_RE.test(tok)) { j += 1; continue; }
          if (WRAPPER_WITH_ARG_RE.test(tok)) { j += 2; continue; }
          break;
        }
        const raw0 = argv[j] || "";
        // Shell control words and punctuation are not programs.
        if (!raw0 || !/^[A-Za-z_$@./\\][\w.@:+-]*$/.test(raw0)) return;
        if (/^(?:if|then|else|elif|fi|for|while|until|do|done|case|esac|function|return|break|continue|local|export)$/i.test(raw0)) return;

        out.push({
          text: stText,
          argv,
          program: raw0.split(/[\\/]/).pop().replace(/\.exe$/i, ""),
          args: argv.slice(j + 1),
          output, outputStatus, notice, ok: !errored,
          key: statements.length > 1 ? `${b.id}#${i}` : (b.id || `synth:${i}`),
          blockId: b.id || null,
          index: i,
          siblings: statements.length,
        });
      });
    }
  }
  return out;
}

/**
 * Flags that consume the next token, and the positional arguments that survive.
 *
 * A shared implementation because getting it wrong is the single most common defect
 * in this codebase: the first "bare" token after `kubectl -n prod-backend` is the
 * NAMESPACE, and reading it as the verb silently dropped eleven of twelve calls.
 */
export function positionalsOf(args, valueFlags = new Set()) {
  const out = [];
  for (let i = 0; i < args.length; i += 1) {
    const t = args[i];
    if (t === "--") break;
    if (t.startsWith("-")) { if (valueFlags.has(t)) i += 1; continue; }
    if (/^(?:\d?[<>]{1,2}&?\d?|[|&;()]{1,2}|\\|\/dev\/null)$/.test(t)) continue;
    out.push(t);
  }
  return out;
}

/** The value of a flag, in `--flag value` or `--flag=value` form, before any `--`. */
export function flagOf(args, ...names) {
  const end = args.indexOf("--");
  const own = end === -1 ? args : args.slice(0, end);
  for (let i = 0; i < own.length; i += 1) {
    for (const n of names) {
      if (own[i] === n) return own[i + 1] || null;
      if (own[i].startsWith(`${n}=`)) return own[i].slice(n.length + 1) || null;
    }
  }
  return null;
}
